/**
 * 日期工具函数
 */

/**
 * 格式化日期
 * @param {Date|String|Number} date - 要格式化的日期
 * @param {String} fmt - 格式化模式，例如 YYYY-MM-DD HH:mm:ss
 * @returns {String} 格式化后的日期字符串
 */
export function formatDate(date, fmt = 'YYYY-MM-DD HH:mm:ss') {
  if (!date) return ''
  
  if (typeof date === 'string') {
    date = new Date(date)
  } else if (typeof date === 'number') {
    date = new Date(date)
  }
  
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return ''
  }
  
  const o = {
    'M+': date.getMonth() + 1, // 月份
    'D+': date.getDate(), // 日
    'H+': date.getHours(), // 小时
    'm+': date.getMinutes(), // 分
    's+': date.getSeconds(), // 秒
    'q+': Math.floor((date.getMonth() + 3) / 3), // 季度
    'S': date.getMilliseconds() // 毫秒
  }
  
  if (/(Y+)/.test(fmt)) {
    fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
  }
  
  for (let k in o) {
    if (new RegExp('(' + k + ')').test(fmt)) {
      fmt = fmt.replace(
        RegExp.$1, 
        RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
      )
    }
  }
  
  return fmt
}

/**
 * 获取日期范围内的天数
 * @param {Date|String|Number} startDate - 开始日期
 * @param {Date|String|Number} endDate - 结束日期
 * @returns {Number} 天数
 */
export function getDaysBetween(startDate, endDate) {
  const start = new Date(typeof startDate === 'string' ? startDate.replace(/-/g, '/') : startDate)
  const end = new Date(typeof endDate === 'string' ? endDate.replace(/-/g, '/') : endDate)
  
  // 重置时间为 00:00:00 以确保只计算天数
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  
  const diff = end.getTime() - start.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1
}

/**
 * 判断两个时间范围是否有重叠
 * @param {String} startA - 范围A的开始时间 (HH:mm 格式)
 * @param {String} endA - 范围A的结束时间 (HH:mm 格式)
 * @param {String} startB - 范围B的开始时间 (HH:mm 格式)
 * @param {String} endB - 范围B的结束时间 (HH:mm 格式)
 * @returns {Boolean} 是否重叠
 */
export function isTimeRangeOverlap(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) return false
  
  // 转换为分钟数以便比较
  const parseTimeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number)
    return hours * 60 + minutes
  }
  
  const startAMinutes = parseTimeToMinutes(startA)
  const endAMinutes = parseTimeToMinutes(endA)
  const startBMinutes = parseTimeToMinutes(startB)
  const endBMinutes = parseTimeToMinutes(endB)
  
  // 检查是否有重叠
  return Math.max(startAMinutes, startBMinutes) < Math.min(endAMinutes, endBMinutes)
}

/**
 * 格式化时间范围
 * @param {String} startTime - 开始时间 (HH:mm 格式)
 * @param {String} endTime - 结束时间 (HH:mm 格式)
 * @returns {String} 格式化后的时间范围
 */
export function formatTimeRange(startTime, endTime) {
  if (!startTime || !endTime) return ''
  return `${startTime} - ${endTime}`
}

/**
 * 将日期格式化为指定类型
 * @param {Date|String|Number} date - 要格式化的日期
 * @param {String} type - 格式类型：'date', 'time', 'datetime'
 * @returns {String} 格式化后的日期字符串
 */
export function formatDateByType(date, type = 'date') {
  const formats = {
    date: 'YYYY-MM-DD',
    time: 'HH:mm:ss',
    datetime: 'YYYY-MM-DD HH:mm:ss',
    short: 'MM-DD'
  }
  
  return formatDate(date, formats[type] || formats.date)
} 